/**
 * Rate Card Engine for Manifests & Receipts.
 *
 * Two kinds of rate card:
 *  - Customer card (one per customer, from the customer): FSA -> Rate Zone -> $/piece, plus accessorials
 *    (weight tiers such as Furniture, and dims surcharges).
 *  - Driver card (one per driver, created by us): Rate Zone -> $/piece, plus weight tiers. Driver cards use the
 *    zones of the customer card the manifest is billed under.
 *
 * Cards come from GET /api/accounting/rate-cards; DEFAULT_RATE_CARDS is the offline fallback.
 * The server re-runs the same rules and returns `manifest.pricing` - when present it wins over the browser preview
 * (see docs/MANIFESTS_BACKEND_HANDOFF.md).
 */

export const DEFAULT_CUSTOMER = 'Amazon';

// Sales tax on customer invoices (Quebec). Drivers are paid without GST/QST.
export const TAXES = [
  { code: 'GST', label: 'GST 5%', rate: 0.05 },
  { code: 'QST', label: 'QST 9.975%', rate: 0.09975 },
];

// FSA -> Rate Zone (subset of the Amazon card; the full table is uploaded on the Rate Card Matrix).
const ZONE1 = ('H1A H1B H1C H1E H1G H1H H1J H1K H1L H1M H1N H1P H1R H1S H1T H1V H1W H1X H1Y H1Z ' +
  'H2A H2B H2G H2H H2J H2K H2L H2T H2W H2X H2Y H2Z H3A H3B H3C H3E H3G H3H H3J H3K H4Z H5A H5B ' +
  'H7A H7B H7C H7E H7J H7R H7X H7Y H9C H9E J4B J4G J4H J4J J4K J4L J4M').split(' ');

/**
 * Weight tiers: the highest tier whose minLb <= weight applies.
 *   mode 'replace' -> piece price = amount (e.g. Furniture rate replaces the zone rate)
 *   mode 'add'     -> piece price = zone rate + amount
 * Dims tiers: the highest tier whose minIn <= longest side applies; always added.
 */
export const DEFAULT_CUSTOMER_CARD = {
  customer: DEFAULT_CUSTOMER,
  zones: {
    1: { rate: 2.0, amazonZone: 8 },
    2: { rate: 3.5, amazonZone: 9 },
    3: { rate: null, amazonZone: 10 },
    4: { rate: 1.85, amazonZone: 11 },
    5: { rate: 1.9, amazonZone: 12 },
    6: { rate: 2.25, amazonZone: 13 },
    7: { rate: 2.75, amazonZone: 14 },
  },
  fsaZone: {
    ...Object.fromEntries(ZONE1.map((f) => [f, 1])),
    // SAMPLE entries for South Shore FSAs seen on the manifest - replaced when the real card is uploaded.
    J2W: 6,
    J2Y: 6,
    J3A: 6,
    J3B: 6,
    J5R: 4,
    J4W: 5,
    J0L: 2,
  },
  accessorials: {
    weightTiers: [
      { code: 'FURNITURE', label: 'Furniture 50–99.99 lb', minLb: 50, mode: 'replace', amount: 6.0 },
      { code: 'FURNITURE_2X', label: '2× Furniture 100+ lb', minLb: 100, mode: 'replace', amount: 12.0 },
    ],
    dimsTiers: [
      { code: '33', label: 'Dims 33–54.99″', minIn: 33, amount: 2.0 },
      { code: '55', label: 'Dims 55″+', minIn: 55, amount: 5.0 },
    ],
  },
};

/** Starting point for a new driver card - every rate must be filled in or uploaded. */
export const newDriverCard = (driver, customerCard = DEFAULT_CUSTOMER_CARD) => ({
  driverId: driver.id,
  driverName: driver.name,
  zones: Object.fromEntries(Object.keys(customerCard.zones).map((z) => [z, null])),
  weightTiers: customerCard.accessorials.weightTiers.map((t) => ({ code: t.code, label: t.label, minLb: t.minLb, mode: 'add', amount: 0 })),
});

/** Same shape as GET /api/accounting/rate-cards. */
export const DEFAULT_RATE_CARDS = {
  customers: { [DEFAULT_CUSTOMER]: DEFAULT_CUSTOMER_CARD },
  drivers: {},
  taxes: TAXES,
};

const CM3_PER_IN3 = 16.387064;

export const getFsa = (postalCode = '') => postalCode.replace(/\s/g, '').toUpperCase().slice(0, 3);

export const extractPostalCode = (address = '') => {
  const m = address.toUpperCase().match(/[A-Z]\d[A-Z]\s?\d[A-Z]\d/);
  return m ? m[0].replace(/\s/g, '') : '';
};

/** Longest side can't be read from volume alone; we use the cube-root as an ESTIMATE (lower bound). */
export const estimateLongestSideIn = (volumeCm3) => {
  if (!volumeCm3) return 0;
  return Math.cbrt(volumeCm3 / CM3_PER_IN3);
};

/** Flags that block billing entirely (no zone price). */
export const isBlockingFlag = (f) => f === 'UNMAPPED_FSA' || f === 'ZONE_NOT_SERVICED' || f === 'NO_CUSTOMER_CARD';

const pickTier = (tiers = [], value, key) =>
  [...tiers].sort((a, b) => b[key] - a[key]).find((t) => t[key] != null && value >= t[key]);

const applyWeight = (base, tier) => (!tier ? base : tier.mode === 'add' ? base + Number(tier.amount || 0) : Number(tier.amount || 0));

export const customerCardFor = (manifest, cards) => cards.customers?.[manifest.customer || DEFAULT_CUSTOMER];
export const driverCardFor = (manifest, cards) => (manifest.driver?.id ? cards.drivers?.[manifest.driver.id] : undefined);

/**
 * Price a single piece for customer billing and driver pay.
 * `review.decision === 'waived'` bills the plain zone rate with no customer accessorials; driver pay is unaffected
 * (the driver still handled the heavy piece).
 */
export const ratePiece = ({ fsa, weightLb, volumeCm3, lengthIn, review }, customerCard, driverCard) => {
  const empty = { zone: null, base: 0, customer: 0, surcharge: 0, accessorial: 0, driverBase: 0, driver: 0, driverRateMissing: false, flagLabels: {} };
  if (!customerCard) return { ...empty, flags: ['NO_CUSTOMER_CARD'] };
  const zone = customerCard.fsaZone[fsa];
  if (!zone) return { ...empty, flags: ['UNMAPPED_FSA'] };
  const base = customerCard.zones[zone]?.rate;
  if (base == null) return { ...empty, zone, flags: ['ZONE_NOT_SERVICED'] };

  const flags = [];
  const flagLabels = {};
  const acc = customerCard.accessorials || {};

  const wTier = pickTier(acc.weightTiers, weightLb, 'minLb');
  let customer = applyWeight(base, wTier);
  if (wTier) {
    flags.push(`WEIGHT_${wTier.code}`);
    flagLabels[`WEIGHT_${wTier.code}`] = wTier.label;
  }

  const longest = lengthIn || estimateLongestSideIn(volumeCm3);
  const dTier = pickTier(acc.dimsTiers, longest, 'minIn');
  let surcharge = 0;
  if (dTier) {
    surcharge = Number(dTier.amount || 0);
    const f = `DIMS_${dTier.code}${lengthIn ? '' : '_EST'}`;
    flags.push(f);
    flagLabels[f] = `${dTier.label} +$${surcharge.toFixed(2)}${lengthIn ? '' : ' (est.)'}`;
  }

  if (flags.length && review?.decision === 'waived') {
    customer = base;
    surcharge = 0;
  }

  // Driver pay: driver's own zone rate, plus the driver's weight tier.
  const driverBase = driverCard?.zones?.[zone];
  const driverRateMissing = driverBase == null;
  const driver = driverRateMissing ? 0 : applyWeight(Number(driverBase), pickTier(driverCard.weightTiers, weightLb, 'minLb'));

  return {
    zone,
    base,
    customer: customer + surcharge,
    surcharge,
    accessorial: customer + surcharge - base,
    driverBase: driverBase ?? 0,
    driver,
    driverRateMissing,
    flags,
    flagLabels,
  };
};

const summarize = (manifest, pieces) => {
  const sum = (k) => pieces.reduce((s, p) => s + (p[k] || 0), 0);
  const exceptions = pieces.filter((p) => p.flags.length > 0);
  const matched = pieces.filter((p) => p.zone && !p.flags.some(isBlockingFlag)).length;
  return {
    pieces,
    exceptions,
    openExceptions: exceptions.filter((p) => !p.review),
    driverRateMissing: pieces.filter((p) => p.zone && p.driverRateMissing).length,
    totals: {
      pieces: pieces.length,
      stops: manifest.stops.length,
      base: sum('base'),
      accessorial: sum('accessorial'),
      customer: sum('customer'),
      driver: sum('driver'),
      surcharges: sum('surcharge'),
      margin: sum('customer') - sum('driver'),
      weightLb: sum('weightLb'),
      matchRate: pieces.length ? matched / pieces.length : 0,
    },
  };
};

/** Rate an entire parsed manifest. Returns enriched pieces + totals. */
export const rateManifest = (manifest, cards = DEFAULT_RATE_CARDS) => {
  const customerCard = customerCardFor(manifest, cards);
  const driverCard = driverCardFor(manifest, cards);
  const pieces = manifest.stops.flatMap((stop) =>
    stop.pieces.map((p) => {
      const postal = extractPostalCode(stop.address);
      const fsa = getFsa(postal);
      return { ...p, stopId: stop.id, name: stop.name, address: stop.address, postal, fsa, ...ratePiece({ fsa, ...p }, customerCard, driverCard) };
    })
  );
  return summarize(manifest, pieces);
};

/**
 * Pricing to display: server pricing (`manifest.pricing.pieces` — live for unposted manifests, frozen at post time)
 * when present, otherwise the browser preview against the current rate cards (demo mode / admins only).
 */
export const getPricing = (manifest, cards = DEFAULT_RATE_CARDS) =>
  manifest.pricing?.pieces ? summarize(manifest, manifest.pricing.pieces) : rateManifest(manifest, cards);

export const money = (n) =>
  (n || 0).toLocaleString('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2 });
